import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useOrders } from './hooks/useOrders';
import { useKanbanOrders } from './hooks/useKanbanOrders';
import OrderCard from './components/OrderCard';
import OrderFiltersBar from './components/OrderFilters';
import OrderStats from './components/OrderStats';
import OrderFormModal from './components/OrderFormModal';
import OrderDetailDrawer from './components/OrderDetailDrawer';
import OrderKanbanView from './components/OrderKanbanView';
import NoteReminders from './components/NoteReminders';
import Navbar from '../home/components/Navbar';
import ServiceTypeChart from './components/ServiceTypeChart';
import { get } from '../../api/client';
import { isDefaultOrderStatusSegment } from '../../utils/orderStatusFilter';
import { exportOrdersPdf, exportOrdersXlsx, printOrdersDocument } from './utils/exportOrders';
import type { ExportSelection } from './utils/exportOrders';
import { useAuthStore } from '../../stores/authStore';
import { useDateUtils } from '../../hooks/useDateUtils';
import { usePaywallGate } from '../../utils/paywall';
import type { OrderItem, StatsData, PaginationMeta } from '../../types';


function Pagination({ page, total, pageSize, onPage }: {
  page: number; total: number; pageSize: number; onPage: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  const pages: (number | string)[] = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      pages.push(i);
    } else if (i === page - 2 || i === page + 2) {
      pages.push('...');
    }
  }

  return (
    <div className="flex items-center justify-center gap-1.5 pt-4">
      <button onClick={() => onPage(page - 1)} disabled={page === 1}
        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors">
        <i className="ri-arrow-left-s-line text-sm" />
      </button>
      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`e${i}`} className="w-8 h-8 flex items-center justify-center text-gray-400 text-sm">…</span>
        ) : (
          <button key={p} onClick={() => onPage(p as number)}
            className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
              page === p ? 'bg-teal-500 text-white border border-teal-500' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}>
            {p}
          </button>
        )
      )}
      <button onClick={() => onPage(page + 1)} disabled={page === Math.ceil(total / pageSize)}
        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors">
        <i className="ri-arrow-right-s-line text-sm" />
      </button>
    </div>
  );
}

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; order: OrderItem; fromDetail?: boolean }
  | { type: 'detail'; order: OrderItem };

export default function OrdersPage() {
  const { profile } = useAuthStore();
  const { getTodayString, getTomorrowString } = useDateUtils();
  // 会员墙判在点导出的这一刻，判不过弹「专属功能 + 是否现在去开通」（见 utils/paywall）
  const gate = usePaywallGate();
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessedNavState = useRef(false);
  const { orders, total, loading, error, page, pageSize, filters, setPage, setFilters, refresh } = useOrders();
  const { columns: kanbanColumns, loading: kanbanLoading, error: kanbanError, refresh: refreshKanban, moveOrder } = useKanbanOrders();

  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [copySuccess, setCopySuccess] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  /**
   * 深链带进来的客户名。它和 `filters.customerId` 分开存，是因为名字只用于显示
   * （筛选 chip 与导出文件名），而后端 query 契约里没有 `customerName` 这个键——
   * 全局 ValidationPipe 带 whitelist，多发一个键只会静默丢掉，不如根本不发。
   * 按 id 配对读，摘掉客户筛选后这一格自然失效，不用另写一次清空。
   */
  const [customerFilter, setCustomerFilter] = useState<{ id: string; name: string } | null>(null);
  const customerFilterName = customerFilter?.id === filters.customerId ? customerFilter.name : '';

  // 默认分段「在途」不算筛选：新用户一进来就是它，若算成筛选，空列表会说成
  // 「没有符合筛选条件的订单」并藏掉「新建第一条订单」的入口。
  const hasActiveFilter = !!(
    filters.keyword || (filters.orderStatus && !isDefaultOrderStatusSegment(filters.orderStatus))
    || filters.paymentStatus
    || filters.dateFrom || filters.dateTo || filters.serviceType || filters.customerId || filters.upcoming
  );

  // 使用 useQuery 获取统计数据
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['orderStats'],
    queryFn: async () => {
      const data = await get<StatsData>('/orders/stats');
      return data;
    },
  });

  // 使用 useQuery 获取待审核订单
  const { data: pendingReviewOrders, refetch: refetchPendingReview } = useQuery({
    queryKey: ['pendingReviewOrders', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const params = {
        customerId: profile.id,
        reviewStatus: '待审核',
        orderBy: 'createdAt',
        orderDirection: 'desc',
        pageSize: 10
      };
      const res = await get<{ data: OrderItem[]; pagination: PaginationMeta }>('/orders', { params });
      return (res?.data ?? []) as OrderItem[];
    },
    enabled: !!profile?.id,
  });

  // 使用 useQuery 获取即将到来的联系人
  const { data: upcomingContacts, refetch: refetchUpcomingContacts } = useQuery({
    queryKey: ['upcomingContacts'],
    queryFn: async () => {
      // query 键名逐字对齐 GET /orders 契约（不传日期 = 后端按 order_start_date 闭区间过滤）
      const params = {
        dateFrom: getTodayString(),
        dateTo: getTomorrowString(),
        orderStatus: ['未执行', '执行中'],
        pageSize: 200,
      };
      const res = await get<{ data: OrderItem[]; pagination: PaginationMeta }>('/orders', { params });
      if (res?.data) {
        // 排除已拒绝的订单（审批状态）
        return res.data
          .filter(o => o.reviewStatus !== '已拒绝')
          .map(o => ({
            name: o.customer?.fullName ?? o.contactPerson ?? '未知客户',
            phone: o.contactPhone ?? o.customer?.phone ?? null,
            person: o.contactPerson,
            date: o.orderStartDate,
            serviceType: o.serviceType?.name ?? null,
          }));
      }
      return [];
    },
  });

  // 优化：使用 useMemo 缓存计算结果
  const upcomingContactsStats = useMemo(() => {
    if (!upcomingContacts) return { todayCount: 0, tomorrowCount: 0, hasPhoneCount: 0 };
    
    const todayStr = getTodayString();
    const tomorrowStr = getTomorrowString();
    
    const todayCount = upcomingContacts.filter(c => c.date === todayStr).length;
    const tomorrowCount = upcomingContacts.filter(c => c.date === tomorrowStr).length;
    const hasPhoneCount = upcomingContacts.filter(c => c.phone).length;
    
    return { todayCount, tomorrowCount, hasPhoneCount };
  }, [upcomingContacts, getTodayString, getTomorrowString]);



  const handleCopyContacts = async () => {
    const todayStr = getTodayString();

    const lines: string[] = ['📞 今明日活动联系方式', '──────────────'];
    let lastDate = '';
    upcomingContacts?.forEach(c => {
      if (c.date !== lastDate) {
        lines.push(c.date === todayStr ? '\n🗓 今日活动：' : '\n🗓 明日活动：');
        lastDate = c.date;
      }
      const parts = [`• ${c.name}`];
      if (c.person && c.person !== c.name) parts.push(`（${c.person}）`);
      if (c.serviceType) parts.push(` [${c.serviceType}]`);
      if (c.phone) parts.push(`  📱 ${c.phone}`);
      lines.push(parts.join(''));
    });
    lines.push('\n──────────────');
    lines.push(`共 ${upcomingContacts?.length || 0} 个活动`);

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
    } catch {
      const el = document.createElement('textarea');
      el.value = lines.join('\n');
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  };

  const handleExport = async (format: 'xlsx' | 'pdf' | 'print' = 'xlsx', options?: ExportSelection) => {
    // 功能目录里 export_csv 指的是「导出订单台账」，PDF 与打印都归 export_print（详情抽屉即按此判定），两格各判各的。
    // 键名保留 export_csv 是因为它已经作为一行写进库、四端都按它映射，改名要洗数据却没有任何用户可见收益。
    // 判定只跑在点导出的这一刻，判不过弹「专属功能 + 是否去开通」（见 utils/paywall）。
    const gateOf = { xlsx: ['exportCsv', '导出 Excel'], pdf: ['exportPrint', '导出 PDF'], print: ['exportPrint', '打印'] } as const;
    const [featureKey, label] = gateOf[format];
    if (!(await gate(featureKey, label))) return;
    // 列表正按某一位客户筛选时，这一份产物就是那个人的台账：customerId 本来就在筛选里，
    // 名字只补进文件名，否则「一个人的欠款单」和「全量」在下载目录里同名。
    const scoped = filters.customerId ? { ...options, customerName: customerFilterName || undefined } : options;
    try {
      if (format === 'pdf') {
        await exportOrdersPdf(filters, scoped);
      } else if (format === 'print') {
        await printOrdersDocument(filters, scoped);
      } else {
        await exportOrdersXlsx(filters, scoped);
      }
    } catch (e) {
      // 导出失败此前是静默的未处理拒绝；透出后端 message（含 403 的会员提示）才有排查线索。
      alert(e instanceof Error && e.message ? e.message : '导出失败，请稍后重试');
    }
  };

  // 处理从各处跳转过来的自动开单（支持 location.state 和 URL查询参数 ?open=orderId）
  useEffect(() => {
    if (hasProcessedNavState.current) return;
    const state = location.state as { openOrderId?: string; filterStartDate?: string } | null;
    const openOrderId = state?.openOrderId;
    const filterStartDate = state?.filterStartDate;
    // 兼容 CustomerOrders.tsx 使用的 URL 查询参数 ?open=orderId
    const urlParams = new URLSearchParams(window.location.search);
    const urlOpenId = urlParams.get('open');
    const effectiveOpenId = openOrderId || urlOpenId;
    // 客户档案页「查看全部订单」的落点：?customer=<Party id>，名字只用于 chip 回显与导出文件名。
    const urlCustomerId = urlParams.get('customer');
    if (!effectiveOpenId && !filterStartDate && !urlCustomerId) return;
    hasProcessedNavState.current = true;
    if (urlCustomerId) {
      setCustomerFilter({ id: urlCustomerId, name: urlParams.get('customerName') ?? '' });
      // 档案页那句「已完成 / 已取消的账请到订单页按该客户查看」就是把人往这里送，
      // 落在默认的「在途」分段上只会拍到「共 0 条」——指向的那批单正好被挡在门外。
      setFilters({ ...filters, customerId: urlCustomerId, orderStatus: '' });
    }
    if (filterStartDate) {
      // 将日期单天筛选默认为 dateFrom=dateTo=该日
      setFilters({ ...filters, dateFrom: filterStartDate, dateTo: filterStartDate });
    }
    if (effectiveOpenId) {
      get<OrderItem>(`/orders/${effectiveOpenId}`).then(data => {
        if (data) setModal({ type: 'detail', order: data });
      }).catch(error => {
        console.error('获取订单详情失败:', error);
      });
    }
    // 清除 state 和 URL 参数避免刷新时重复触发
    window.history.replaceState({}, document.title, window.location.pathname);
  }, [location.state, filters, setFilters]);

  const handleSaved = async () => {
    refresh();
    refreshKanban();
    refetchStats();
    refetchPendingReview();
    refetchUpcomingContacts();
    setCopySuccess(false);

    // 若从详情页进入编辑，保存后刷新数据并返回详情抽屉
    if (modal.type === 'edit' && modal.fromDetail) {
      try {
        const data = await get<OrderItem>(`/orders/${modal.order.id}`);
        if (data) {
          setModal({ type: 'detail', order: data });
        } else {
          setModal({ type: 'none' });
        }
      } catch (error) {
        console.error('获取订单详情失败:', error);
        setModal({ type: 'none' });
      }
      return;
    }
    setModal({ type: 'none' });
  };

  /**
   * 提交撞 409：弹窗留着让用户改，但背后的列表已经是过期快照，得先刷成最新，
   * 否则用户会对着「自己没提交成功、列表里却凭空多出一张单」的错觉反复点提交。
   */
  const handleConflict = () => {
    refresh();
    refreshKanban();
    refetchStats();
    refetchPendingReview();
    refetchUpcomingContacts();
  };

  /** 抽屉里取消订单成功：这张单退出列表与核算，刷新成套查询（订单不能删除，所以没有「删除后」这条通路） */
  const handleCancelled = () => {
    setModal({ type: 'none' });
    refresh();
    refreshKanban();
    refetchStats();
    refetchPendingReview();
    refetchUpcomingContacts();
  };

  /**
   * 抽屉里改了订单（审核通过/驳回、改派客户等）之后的刷新。
   * 必须与 handleSaved/handleCancelled 成套：顶部「待我审核」网格是独立的 pendingReviewOrders 查询，
   * 漏刷它就会出现「审核成功了，网格上那张卡还停在老状态」——用户看到的正是这个。
   */
  const handleOrderUpdated = useCallback(async () => {
    refresh();
    refreshKanban();
    refetchStats();
    refetchPendingReview();
    refetchUpcomingContacts();

    // 重新拉取最新订单数据更新抽屉
    if (modal.type === 'detail') {
      const orderId = modal.order.id;
      get<OrderItem>(`/orders/${orderId}`).then(data => {
        if (data) {
          setModal({ type: 'detail', order: data });
        }
      }).catch(error => {
        console.error('获取订单详情失败:', error);
      });
    }

  }, [modal, refresh, refreshKanban, refetchStats, refetchPendingReview, refetchUpcomingContacts]);

  const handleCardClick = (order: OrderItem) => setModal({ type: 'detail', order });

  // 记录是否从详情页进入编辑
  const handleEdit = (order: OrderItem) => {
    setModal(prev => ({ type: 'edit', order, fromDetail: prev.type === 'detail' }));
  };

  const closeModal = () => setModal({ type: 'none' });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 pb-28 md:pb-6 space-y-4">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">订单管理</h1>
            <p className="text-sm text-gray-400 mt-0.5">查看和管理所有服务订单</p>
          </div>
          <div className="flex items-center gap-2">
            {/* 视图切换 */}
            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'list' ? 'bg-teal-500 text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-list-check-2 text-sm" />
                </div>
                <span className="hidden sm:inline">列表</span>
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'kanban' ? 'bg-teal-500 text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-kanban-view text-sm" />
                </div>
                <span className="hidden sm:inline">看板</span>
              </button>
            </div>
            <button onClick={() => { refresh(); refreshKanban(); }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-refresh-line text-sm" />
              </div>
              <span className="hidden sm:inline">刷新</span>
            </button>
            <button
              onClick={() => setModal({ type: 'create' })}
              className="hidden sm:flex items-center gap-1.5 px-4 py-2 text-sm bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line text-sm" />
              </div>
              新建订单
            </button>
          </div>
        </div>

        {/* ── 备注提醒 ── */}
        <NoteReminders onOpenOrder={(orderId) => {
          get<OrderItem>(`/orders/${orderId}`).then(data => {
            if (data) setModal({ type: 'detail', order: data });
          }).catch(error => {
            console.error('获取订单详情失败:', error);
          });
        }} />

        {/* ── 今明日活动提醒 ── */}
        {upcomingContacts && upcomingContacts.length > 0 && (
          <div className="bg-white border border-teal-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg shrink-0">
                <i className="ri-calendar-event-line text-teal-500 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800">今明日活动提醒</p>
                <p className="text-xs text-gray-400 truncate">
                  {upcomingContactsStats.todayCount} 个今日·{upcomingContactsStats.tomorrowCount} 个明日 &nbsp;|&nbsp;
                  {upcomingContactsStats.hasPhoneCount} 个有联系方式
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
                {upcomingContacts.slice(0, 4).map((c, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs bg-gray-50 border border-gray-100 rounded-full px-2 py-0.5 text-gray-600 shrink-0 whitespace-nowrap">
                    {c.name}{c.phone ? ` · ${c.phone}` : ''}
                  </span>
                ))}
                {upcomingContacts.length > 4 && (
                  <span className="text-xs text-gray-400">+{upcomingContacts.length - 4} 位</span>
                )}
              </div>
            </div>
            <button
              onClick={handleCopyContacts}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg font-medium cursor-pointer transition-all whitespace-nowrap shrink-0 ${
                copySuccess
                  ? 'bg-teal-500 text-white'
                  : 'bg-teal-500 hover:bg-teal-600 text-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${copySuccess ? 'ri-check-line' : 'ri-phone-line'} text-sm`} />
              </div>
              {copySuccess ? '已复制！' : '一键复制联系方式'}
            </button>
          </div>
        )}

        {/* ── 待我审核区块 ── */}
        {pendingReviewOrders && pendingReviewOrders.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                <i className="ri-shield-line text-amber-500 text-sm" />
              </div>
              <p className="text-sm font-semibold text-amber-700">待我审核</p>
              <span className="ml-1 text-xs px-2 py-0.5 bg-amber-200 text-amber-700 rounded-full">{pendingReviewOrders.length}</span>
              <p className="ml-2 text-xs text-amber-500">对方向你发送了订单确认请求，请查阅并确认</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {pendingReviewOrders.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onClick={handleCardClick}
                  onEdit={handleEdit}
                  onServiceTypeClick={(name) => setFilters({ ...filters, serviceType: name })}
                />
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <OrderStats stats={stats} loading={statsLoading} />

        {/* 服务类型收入分布图 */}
        <ServiceTypeChart />

        {/* Filters — 仅列表模式显示 */}
        {viewMode === 'list' && (
          <OrderFiltersBar filters={filters} onChange={setFilters} total={total} onExport={handleExport} customerName={customerFilterName} />
        )}

        {/* Error */}
        {error && viewMode === 'list' && (
          <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-sm text-rose-600 flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-error-warning-line" /></div>
            {error}
          </div>
        )}

        {/* ── 看板视图 ── */}
        {viewMode === 'kanban' && (
          <OrderKanbanView
            columns={kanbanColumns}
            loading={kanbanLoading}
            error={kanbanError}
            onMoveOrder={async (orderId, newStatus) => {
              // 不把 promise 交回看板，失败就只有 unhandled rejection，界面静默回弹
              await moveOrder(orderId, newStatus);
              refresh();
              refetchStats();
            }}
            onCardClick={(order) => setModal({ type: 'detail', order })}
          />
        )}

        {/* ── 列表视图 ── */}
        {viewMode === 'list' && (
          loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 animate-pulse">
                  <div className="h-4 bg-gray-100 rounded w-2/3 mb-3" />
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-full shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 bg-gray-100 rounded w-3/4" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="space-y-2 mb-3">
                    <div className="h-3 bg-gray-100 rounded" />
                    <div className="h-3 bg-gray-100 rounded w-4/5" />
                  </div>
                  <div className="flex justify-between pt-3 border-t border-gray-50">
                    <div className="h-5 bg-gray-100 rounded w-1/3" />
                    <div className="h-5 bg-gray-100 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl flex flex-col items-center justify-center py-20">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                <i className="ri-file-list-3-line text-2xl text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium mb-1">暂无订单</p>
              <p className="text-sm text-gray-400 mb-5">
                {hasActiveFilter ? '没有符合筛选条件的订单' : '还没有任何订单数据'}
              </p>
              {!hasActiveFilter && (
                <button
                  onClick={() => setModal({ type: 'create' })}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                  新建第一条订单
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                {orders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onClick={handleCardClick}
                    onEdit={handleEdit}
                    onServiceTypeClick={(name) => setFilters({ ...filters, serviceType: name })}
                  />
                ))}
              </div>
              <Pagination page={page} total={total} pageSize={pageSize} onPage={setPage} />
            </>
          )
        )}
      </main>

      {/* Mobile FAB */}
      <div className="fixed bottom-20 right-4 z-40 sm:hidden">
        <button
          onClick={() => setModal({ type: 'create' })}
          className="w-13 h-13 bg-teal-500 hover:bg-teal-600 text-white rounded-full flex items-center justify-center cursor-pointer transition-all active:scale-95 px-4 py-3 gap-1.5"
          style={{ width: '56px', height: '56px' }}
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-add-line text-xl" />
          </div>
        </button>
      </div>

      {/* Modals */}
      {modal.type === 'create' && (
        <OrderFormModal mode="create" onClose={closeModal} onSaved={handleSaved} onConflict={handleConflict} />
      )}
      {modal.type === 'edit' && (
        <OrderFormModal mode="edit" initial={modal.order} onClose={closeModal} onSaved={handleSaved} onConflict={handleConflict} />
      )}
      {modal.type === 'detail' && (
        <OrderDetailDrawer
          // 抽屉里十几处本地态只在挂载时从 props.order 播种，关联订单跳转会在 type 不变时换单；
          // 不按 id 重挂就会拿旧单的档期与已收款配新单的表头。
          key={modal.order.id}
          order={modal.order}
          onClose={closeModal}
          onEdit={handleEdit}
          onCancelled={handleCancelled}
          onUpdated={handleOrderUpdated}
          onNavigateToOrder={(orderId) => {
            get<OrderItem>(`/orders/${orderId}`).then(data => {
              if (data) setModal({ type: 'detail', order: data });
            }).catch(error => {
              console.error('获取关联订单详情失败:', error);
            });
          }}
          onNavigateToCustomer={(customerId) => {
            closeModal();
            navigate(`/customers/${customerId}`);
          }}
        />
      )}
    </div>
  );
}
